import { channelClient, RequestPolicies } from 'shared/channels';
import { createEntityClient, runQueuedMutation } from 'shared/channels/entity';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import type { InspectionTemplateModel } from 'shared/domain/template/inspectionTemplateModel';
import { InspectionTemplateStatus } from 'shared/domain/template/templateStatus';
import type { ChecklistItemInDto, InspectionTemplateInDto } from 'shared/dtos/in/template';
import { inspectionTemplateContract, type InspectionTemplateRequests } from './inspectionTemplateContract';

/** A change as the page describes it; the client adds the `uniqueId` correlating its outcome. */
export type InspectionTemplateChange<TMethod extends keyof InspectionTemplateRequests> = Omit<
  InspectionTemplateRequests[TMethod]['request'],
  'uniqueId'
>;

/** The template a change is about, in its project. */
export type InspectionTemplateRef = Pick<InspectionTemplateChange<'edit'>, 'templateId' | 'projectId'>;

/** Template and checklist item creates share one outcome type, told apart by their payload. */
function isChecklistItemList(created: InspectionTemplateInDto | ChecklistItemInDto[]): created is ChecklistItemInDto[] {
  return Array.isArray(created);
}

/**
 * Inspection templates of the selected project as served by the service worker
 * (none for standard users), with their checklist items. Changes resolve once
 * the server accepted them and reject with its error (`ChannelRequestError.messageId`).
 */
export const inspectionTemplateClient = {
  ...createEntityClient(inspectionTemplateContract),

  /** One template for its details view, archived ones included; rejects when there is none. */
  getOneById(id: string): Promise<InspectionTemplateModel> {
    return channelClient.request(inspectionTemplateContract, 'getOneById', { id }, RequestPolicies.read);
  },

  /** Resolves with the created template. */
  async create(change: InspectionTemplateChange<'create'>): Promise<InspectionTemplateInDto> {
    const created = await runQueuedMutation(
      inspectionTemplateContract,
      'create',
      (uniqueId) => ({ ...change, uniqueId }),
      DomainMessagesTypes.createdEntity
    );
    if (isChecklistItemList(created)) {
      throw new Error('A template create was answered with checklist items');
    }
    return created;
  },

  async edit(change: InspectionTemplateChange<'edit'>): Promise<void> {
    await runQueuedMutation(
      inspectionTemplateContract,
      'edit',
      (uniqueId) => ({ ...change, uniqueId }),
      DomainMessagesTypes.updatedEntity
    );
  },

  /** An edit to the published status. */
  publish({ templateId, projectId }: InspectionTemplateRef): Promise<void> {
    return inspectionTemplateClient.edit({
      templateId,
      projectId,
      inspectionTemplateOutDto: { status: InspectionTemplateStatus.published },
    });
  },

  /** Archives the template. */
  async remove(change: InspectionTemplateChange<'remove'>): Promise<void> {
    await runQueuedMutation(
      inspectionTemplateContract,
      'remove',
      (uniqueId) => ({ ...change, uniqueId }),
      DomainMessagesTypes.deletedEntity
    );
  },

  async restore(change: InspectionTemplateChange<'restore'>): Promise<void> {
    await runQueuedMutation(
      inspectionTemplateContract,
      'restore',
      (uniqueId) => ({ ...change, uniqueId }),
      DomainMessagesTypes.updatedEntity
    );
  },

  /** Resolves with the created checklist items. */
  async createChecklistItems(change: InspectionTemplateChange<'createChecklistItems'>): Promise<ChecklistItemInDto[]> {
    const created = await runQueuedMutation(
      inspectionTemplateContract,
      'createChecklistItems',
      (uniqueId) => ({ ...change, uniqueId }),
      DomainMessagesTypes.createdEntity
    );
    if (!isChecklistItemList(created)) {
      throw new Error('A checklist items create was answered with a template');
    }
    return created;
  },

  async editChecklistItem(change: InspectionTemplateChange<'editChecklistItem'>): Promise<void> {
    await runQueuedMutation(
      inspectionTemplateContract,
      'editChecklistItem',
      (uniqueId) => ({ ...change, uniqueId }),
      DomainMessagesTypes.updatedEntity
    );
  },

  async removeChecklistItem(change: InspectionTemplateChange<'removeChecklistItem'>): Promise<void> {
    await runQueuedMutation(
      inspectionTemplateContract,
      'removeChecklistItem',
      (uniqueId) => ({ ...change, uniqueId }),
      DomainMessagesTypes.deletedEntity
    );
  },

  async reorderChecklistItem(change: InspectionTemplateChange<'reorderChecklistItem'>): Promise<void> {
    await runQueuedMutation(
      inspectionTemplateContract,
      'reorderChecklistItem',
      (uniqueId) => ({ ...change, uniqueId }),
      DomainMessagesTypes.updatedEntity
    );
  },
};
